<template>
  <div class="system-setting">
    <div class="page-header">
      <h2 class="page-title">系统设置</h2>
      <p class="page-desc">配置小程序、微信支付等系统参数</p>
    </div>

    <div class="setting-container">
      <!-- 小程序配置 -->
      <el-card class="setting-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <div class="card-header-title">
              <el-icon>
                <Phone />
              </el-icon>
              <span>小程序配置</span>
            </div>
            <div class="card-header-actions">
              <el-button type="primary" link @click="openUploadCodeDialog" size="small">
                <el-icon>
                  <Upload />
                </el-icon> 上传代码
              </el-button>
              <el-button type="primary" link @click="openMiniProgramDialog" size="small">
                <el-icon>
                  <Edit />
                </el-icon> 编辑
              </el-button>
            </div>
          </div>
        </template>
        <div class="config-content">
          <div class="config-item">
            <span class="config-label">AppID</span>
            <span class="config-value">{{ miniProgramConfig.appid || '未配置' }}</span>
          </div>
          <div class="config-item">
            <span class="config-label">AppSecret</span>
            <span class="config-value">
              <span v-if="miniProgramConfig.appsecret">●●●●●●●●</span>
              <span v-else>未配置</span>
            </span>
          </div>
          <div class="config-item">
            <span class="config-label">上传密钥</span>
            <span class="config-value">{{ miniProgramConfig.upload_key ? '已配置' : '未配置' }}</span>
          </div>
        </div>
      </el-card>

      <!-- 微信支付配置 -->
      <el-card class="setting-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <div class="card-header-title">
              <el-icon>
                <Wallet />
              </el-icon>
              <span>微信支付配置</span>
            </div>
            <div class="card-header-actions">
              <el-button type="primary" link @click="goToPayTest" size="small">
                <el-icon>
                  <CreditCard />
                </el-icon> 测试支付
              </el-button>
              <el-button type="primary" link @click="openPayDialog" size="small">
                <el-icon>
                  <Edit />
                </el-icon> 编辑
              </el-button>
            </div>
          </div>
        </template>
        <div class="config-content">
          <div class="config-item">
            <span class="config-label">商户号</span>
            <span class="config-value">{{ payConfig.mchid || '未配置' }}</span>
          </div>
          <div class="config-item">
            <span class="config-label">API密钥</span>
            <span class="config-value">
              <span v-if="payConfig.api_key">●●●●●●●●</span>
              <span v-else>未配置</span>
            </span>
          </div>
          <div class="config-item">
            <span class="config-label">APIv3密钥</span>
            <span class="config-value">
              <span v-if="payConfig.api_v3_key">●●●●●●●●</span>
              <span v-else>未配置</span>
            </span>
          </div>
          <div class="config-item">
            <span class="config-label">商户证书</span>
            <span class="config-value">{{ payConfig.cert_files && Object.keys(payConfig.cert_files).length > 0 ? '已配置' :
              '未配置'
            }}</span>
          </div>
          <div class="config-item">
            <span class="config-label">支付模式</span>
            <span class="config-value">{{ payConfig.pay_mode === 'production' ? '生产模式' : '沙箱模式' }}</span>
          </div>
        </div>
      </el-card>

      <!-- 特约商户进件 -->
      <el-card class="setting-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <div class="card-header-title">
              <el-icon>
                <DocumentAdd />
              </el-icon>
              <span>特约商户进件</span>
            </div>
            <div class="card-header-actions">
              <el-button v-if="latestApply && latestApply.status === 1" type="primary" link @click="queryApplyStatus"
                :loading="querying" size="small">
                <el-icon>
                  <Refresh />
                </el-icon> 刷新状态
              </el-button>
              <!-- 草稿状态：显示编辑按钮 -->
              <el-button v-if="latestApply && latestApply.status === 0" type="primary" link @click="openApplyDialog"
                size="small">
                <el-icon>
                  <Edit />
                </el-icon> 编辑草稿
              </el-button>
              <!-- 无申请或已驳回/已取消：显示申请按钮 -->
              <el-button v-if="!latestApply || [3, 5].includes(latestApply.status)" type="primary" link
                @click="openApplyDialog" size="small">
                <el-icon>
                  <Plus />
                </el-icon> 申请进件
              </el-button>
            </div>
          </div>
        </template>
        <div class="config-content">
          <div v-if="latestApply" class="apply-status">
            <el-alert :title="statusTitle" :type="statusType" :closable="false" show-icon>
              <template #default>
                <div class="status-detail">
                  <p v-if="latestApply.merchant_name">商户名称：{{ latestApply.merchant_name }}</p>
                  <p v-if="latestApply.sub_mchid">子商户号：{{ latestApply.sub_mchid }}</p>
                  <p v-if="latestApply.applyment_id">申请单号：{{ latestApply.applyment_id }}</p>
                  <p v-if="latestApply.reject_reason" class="reject-reason">驳回原因：{{ latestApply.reject_reason }}</p>
                  <!-- 只有当状态是待确认或待签约时才显示签约按钮 -->
                  <p v-if="latestApply.sign_url && shouldShowSignButton(latestApply.wx_applyment_state)">
                    <el-link type="primary" @click="showSignQrCode(latestApply.sign_url)">点击前往签约</el-link>
                  </p>
                  <!-- 如果已完成，显示成功提示 -->
                  <p v-if="latestApply.wx_applyment_state === 'APPLYMENT_STATE_FINISHED'" class="success-tip">
                    <el-tag type="success">入驻已完成！</el-tag>
                  </p>
                </div>
              </template>
            </el-alert>
            <!-- 草稿或驳回状态：可以取消 -->
            <div v-if="latestApply.status === 0 || latestApply.status === 3" class="apply-actions">
              <el-button type="danger" link @click="cancelApply">取消申请</el-button>
            </div>
          </div>
          <div v-else class="no-apply">
            <el-empty description="暂无进件申请，点击上方按钮申请" :image-size="80" />
          </div>
        </div>
      </el-card>
    </div>

    <!-- 小程序配置弹窗 -->
    <el-dialog v-model="miniProgramDialogVisible" title="编辑小程序配置" width="550px" destroy-on-close>
      <el-form :model="miniProgramForm" label-width="110px">
        <el-form-item label="AppID" required>
          <el-input v-model="miniProgramForm.appid" placeholder="请输入小程序AppID" />
        </el-form-item>
        <el-form-item label="AppSecret" required>
          <el-input v-model="miniProgramForm.appsecret" type="password" placeholder="请输入小程序AppSecret" show-password />
          <div class="form-tip">修改时需要重新输入，留空表示保持不变</div>
        </el-form-item>
        <el-form-item label="上传密钥">
          <el-input v-model="miniProgramForm.upload_key" type="textarea" :rows="3" placeholder="请输入小程序上传密钥（可选）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="miniProgramDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveMiniProgramConfig" :loading="saving">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 微信支付配置弹窗 -->
    <el-dialog v-model="payDialogVisible" title="编辑微信支付配置" width="650px" destroy-on-close>
      <el-form :model="payForm" label-width="110px">
        <el-form-item label="商户号" required>
          <el-input v-model="payForm.mchid" placeholder="请输入微信支付商户号" />
        </el-form-item>
        <el-form-item label="API密钥" required>
          <el-input v-model="payForm.api_key" type="password" placeholder="请输入API密钥（32位）" show-password />
          <div class="form-tip">修改时需要重新输入，留空表示保持不变</div>
        </el-form-item>
        <el-form-item label="APIv3密钥">
          <el-input v-model="payForm.api_v3_key" type="password" placeholder="请输入APIv3密钥（选填）" show-password />
        </el-form-item>
        <el-form-item label="商户证书">
          <el-upload :file-list="certFiles" action="#" :limit="2" accept=".pem" :auto-upload="false"
            :on-change="handleCertChange" :on-remove="handleCertRemove">
            <el-button type="primary" link>
              <el-icon>
                <Upload />
              </el-icon> 上传证书
            </el-button>
            <template #tip>
              <div class="upload-tip">支持 .pem 格式，最多上传2个文件（apiclient_cert.pem 和 apiclient_key.pem）</div>
            </template>
          </el-upload>
          <div v-if="Object.keys(certData).length > 0" class="cert-list">
            <div v-for="(url, name) in certData" :key="name" class="cert-item">
              <span>{{ name }}</span>
              <el-tag size="small" type="success">已上传</el-tag>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="支付模式">
          <el-radio-group v-model="payForm.pay_mode">
            <el-radio label="sandbox">沙箱模式（测试）</el-radio>
            <el-radio label="production">生产模式（正式）</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="payDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="savePayConfig" :loading="saving">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 上传代码弹窗 -->
    <el-dialog v-model="uploadCodeDialogVisible" title="上传小程序代码" width="650px" destroy-on-close
      @close="resetUploadForm">
      <el-form :model="uploadForm" label-width="120px">
        <el-form-item label="小程序AppID" required>
          <el-input v-model="uploadForm.appid" disabled placeholder="请先配置小程序AppID" />
          <div class="form-tip">从小程序配置中自动获取</div>
        </el-form-item>
        <el-form-item label="上传私钥" required>
          <el-input v-model="uploadForm.privateKeyContent" type="textarea" :rows="8" disabled placeholder="请先配置上传密钥" />
          <div class="form-tip">从小程序配置中自动获取（upload_key）</div>
        </el-form-item>
        <el-form-item label="API域名" required>
          <el-input v-model="uploadForm.api_url" disabled />
          <div class="form-tip">自动使用系统配置的API域名</div>
        </el-form-item>
        <el-form-item label="商家ID" required>
          <el-input-number v-model="uploadForm.default_store_id" :disabled="true" :min="1" />
          <div class="form-tip">自动使用当前商家ID</div>
        </el-form-item>
      </el-form>

      <div v-if="uploading" class="upload-progress">
        <el-progress :percentage="uploadProgress" :status="uploadProgressStatus" />
        <div class="upload-log">
          <div v-for="(log, index) in uploadLogs" :key="index" class="log-line">{{ log }}</div>
        </div>
      </div>

      <template #footer>
        <el-button @click="uploadCodeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitUploadCode" :loading="uploading" :disabled="uploading">
          {{ uploading ? '上传中...' : '开始上传' }}
        </el-button>
      </template>
    </el-dialog>

    <!-- ========== 进件申请弹窗 - 完整版 ========== -->
    <el-dialog v-model="applyDialogVisible" title="特约商户进件申请" width="950px" destroy-on-close @close="resetApplyForm"
      :close-on-click-modal="false" :close-on-press-escape="false">
      <el-form :model="applyForm" :rules="applyRules" ref="applyFormRef" label-width="150px">

        <!-- ========== 1. 主体信息 ========== -->
        <el-divider content-position="left">主体信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="主体类型" prop="subject_type">
              <el-select v-model="applyForm.subject_type" placeholder="请选择主体类型" style="width: 100%">
                <el-option label="企业" value="SUBJECT_TYPE_ENTERPRISE" />
                <el-option label="个体工商户" value="SUBJECT_TYPE_INDIVIDUAL" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="商户全称" prop="merchant_name">
              <el-input v-model="applyForm.merchant_name" placeholder="与营业执照一致" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="商户简称" prop="merchant_shortname">
              <el-input v-model="applyForm.merchant_shortname" placeholder="将展示给用户" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="营业执照号" prop="license_number">
              <el-input v-model="applyForm.license_number" placeholder="统一社会信用代码" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="法人姓名" prop="legal_person">
              <el-input v-model="applyForm.legal_person" placeholder="与身份证一致" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="营业执照有效期开始" prop="license_period_begin">
              <el-date-picker v-model="applyForm.license_period_begin" type="date" placeholder="开始日期"
                value-format="YYYY-MM-DD" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="营业执照有效期结束" prop="license_period_end">
              <el-date-picker v-model="applyForm.license_period_end_date" type="date" placeholder="结束日期"
                value-format="YYYY-MM-DD" style="width: 100%" :disabled="applyForm.license_long_term" />
              <div style="margin-top: 8px;">
                <el-checkbox v-model="applyForm.license_long_term">长期有效</el-checkbox>
              </div>
              <div class="form-tip">勾选"长期有效"或选择具体日期</div>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="营业执照地址" prop="company_address">
          <el-input v-model="applyForm.company_address" placeholder="与营业执照上的地址一致" />
        </el-form-item>
        <!-- 营业执照 -->
        <el-form-item label="营业执照" prop="business_license_media_id">
          <div class="upload-wrapper" v-if="!licenseFileList.length">
            <el-upload :file-list="licenseFileList" :limit="1" accept="image/jpeg,image/png,image/bmp"
              :auto-upload="false" :on-change="(file) => handleImageChange(file, 'license')" list-type="picture-card">
              <el-icon>
                <Plus />
              </el-icon>
            </el-upload>
            <div class="upload-tip">请上传营业执照（JPG/BMP/PNG，≤5M）</div>
          </div>
          <div v-else class="uploaded-preview">
            <div class="preview-image">
              <img :src="licenseFileList[0].url" class="preview-img" />
              <div class="preview-actions">
                <el-button type="danger" size="small" @click="handleImageRemove('license')">删除</el-button>
                <el-button type="primary" size="small" @click="reUploadImage('license')">重新上传</el-button>
              </div>
            </div>
          </div>
        </el-form-item>

        <!-- ========== 2. 法人/经营者身份信息 ========== -->
        <el-divider content-position="left">法人/经营者身份信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="身份证姓名" prop="id_card_name">
              <el-input v-model="applyForm.id_card_name" placeholder="与身份证一致" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="身份证号码" prop="id_card_number">
              <el-input v-model="applyForm.id_card_number" placeholder="18位身份证号" />
            </el-form-item>
          </el-col>
        </el-row>
        <!-- 新增：身份证地址 -->
        <el-form-item label="身份证地址" prop="id_card_address">
          <el-input v-model="applyForm.id_card_address" placeholder="与身份证上的地址一致" />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="身份证有效期开始" prop="card_period_begin">
              <el-date-picker v-model="applyForm.card_period_begin" type="date" placeholder="开始日期"
                value-format="YYYY-MM-DD" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="身份证有效期结束" prop="card_period_end">
              <el-date-picker v-model="applyForm.card_period_end_date" type="date" placeholder="结束日期"
                value-format="YYYY-MM-DD" style="width: 100%" :disabled="applyForm.card_long_term" />
              <div style="margin-top: 8px;">
                <el-checkbox v-model="applyForm.card_long_term">长期有效</el-checkbox>
              </div>
              <div class="form-tip">勾选"长期有效"或选择具体日期</div>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="是否为最终受益人" prop="owner">
          <el-switch v-model="applyForm.owner" active-text="是" inactive-text="否" />
          <div class="form-tip">通常为"是"，表示法人/经营者为最终受益人</div>
        </el-form-item>
        <!-- 身份证照片 -->
        <el-form-item label="身份证照片" prop="id_card_images">
          <div class="id-card-uploads">
            <!-- 人像面 -->
            <div class="upload-item" v-if="!idCardFrontFileList.length">
              <div class="upload-wrapper">
                <el-upload :file-list="idCardFrontFileList" :limit="1" accept="image/jpeg,image/png,image/bmp"
                  :auto-upload="false" :on-change="(file) => handleImageChange(file, 'idCardFront')"
                  list-type="picture-card">
                  <el-icon>
                    <Plus />
                  </el-icon>
                </el-upload>
                <span class="upload-label">人像面</span>
              </div>
            </div>
            <div v-else class="uploaded-item">
              <div class="preview-image">
                <img :src="idCardFrontFileList[0].url" class="preview-img" />
                <div class="preview-actions">
                  <el-button type="danger" size="small" @click="handleImageRemove('idCardFront')">删除</el-button>
                  <el-button type="primary" size="small" @click="reUploadImage('idCardFront')">重新上传</el-button>
                </div>
                <span class="upload-label">人像面</span>
              </div>
            </div>

            <!-- 国徽面 -->
            <div class="upload-item" style="margin-left: 20px;" v-if="!idCardBackFileList.length">
              <div class="upload-wrapper">
                <el-upload :file-list="idCardBackFileList" :limit="1" accept="image/jpeg,image/png,image/bmp"
                  :auto-upload="false" :on-change="(file) => handleImageChange(file, 'idCardBack')"
                  list-type="picture-card">
                  <el-icon>
                    <Plus />
                  </el-icon>
                </el-upload>
                <span class="upload-label">国徽面</span>
              </div>
            </div>
            <div v-else class="uploaded-item">
              <div class="preview-image">
                <img :src="idCardBackFileList[0].url" class="preview-img" />
                <div class="preview-actions">
                  <el-button type="danger" size="small" @click="handleImageRemove('idCardBack')">删除</el-button>
                  <el-button type="primary" size="small" @click="reUploadImage('idCardBack')">重新上传</el-button>
                </div>
                <span class="upload-label">国徽面</span>
              </div>
            </div>
          </div>
        </el-form-item>

        <!-- ========== 3. 经营信息 ========== -->
        <el-divider content-position="left">经营信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="客服电话" prop="service_phone">
              <el-input v-model="applyForm.service_phone" placeholder="座机或手机号" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="经营场景" prop="sales_scenes_type">
              <el-select v-model="applyForm.sales_scenes_type" placeholder="请选择经营场景" style="width: 100%">
                <el-option label="线下门店" value="SALES_SCENES_STORE" />
                <el-option label="小程序" value="SALES_SCENES_MINI_PROGRAM" />
                <el-option label="公众号" value="SALES_SCENES_MP" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 线下门店场景 -->
        <template v-if="applyForm.sales_scenes_type === 'SALES_SCENES_STORE'">
          <el-form-item label="门店名称" prop="biz_store_name">
            <el-input v-model="applyForm.biz_store_name" placeholder="如：大郎烧饼" />
          </el-form-item>
          <el-form-item label="经营地址详情" prop="biz_store_address">
            <el-input v-model="applyForm.biz_store_address" placeholder="如：南山区xx大厦x层xxxx室" />
          </el-form-item>
          <el-form-item label="门店门头照片" prop="store_entrance_pic">
            <div class="upload-wrapper">
              <el-upload :file-list="storeEntranceFileList" :limit="1" accept="image/jpeg,image/png,image/bmp"
                :auto-upload="false" :on-change="(file) => handleImageChange(file, 'storeEntrance')"
                :on-remove="() => handleImageRemove('storeEntrance')" list-type="picture-card">
                <el-icon>
                  <Plus />
                </el-icon>
              </el-upload>
              <div class="upload-tip">请上传门店门头照片</div>
            </div>
          </el-form-item>
          <el-form-item label="门店内部照片" prop="indoor_pic">
            <div class="upload-wrapper">
              <el-upload :file-list="indoorFileList" :limit="1" accept="image/jpeg,image/png,image/bmp"
                :auto-upload="false" :on-change="(file) => handleImageChange(file, 'indoor')"
                :on-remove="() => handleImageRemove('indoor')" list-type="picture-card">
                <el-icon>
                  <Plus />
                </el-icon>
              </el-upload>
              <div class="upload-tip">请上传门店内部照片</div>
            </div>
          </el-form-item>
          <el-form-item label="商家AppID" prop="biz_sub_appid">
            <el-input v-model="applyForm.biz_sub_appid" placeholder="已认证的服务号/小程序/APP的AppID" />
            <div class="form-tip">线下门店场景建议填写，用于开通支付权限</div>
          </el-form-item>
        </template>

        <!-- 小程序场景 -->
        <template v-if="applyForm.sales_scenes_type === 'SALES_SCENES_MINI_PROGRAM'">
          <el-form-item label="小程序AppID" prop="mini_program_sub_appid">
            <el-input v-model="applyForm.mini_program_sub_appid" placeholder="请填写已认证的小程序AppID" />
          </el-form-item>
          <!-- 小程序截图 -->
          <el-form-item label="小程序截图" prop="mini_program_pics">
            <div class="upload-wrapper">
              <el-upload :file-list="miniProgramPicsFileList" :limit="5" accept="image/jpeg,image/png,image/bmp"
                :auto-upload="false" :on-change="(file) => handleImageChange(file, 'miniProgramPics')"
                :on-remove="(file) => handleImageRemove('miniProgramPics', file)" list-type="picture-card" multiple>
                <el-icon>
                  <Plus />
                </el-icon>
              </el-upload>
              <div class="upload-tip">最多上传5张，请提供展示商品/服务的页面截图</div>
            </div>
          </el-form-item>
        </template>

        <!-- 公众号场景 -->
        <template v-if="applyForm.sales_scenes_type === 'SALES_SCENES_MP'">
          <el-form-item label="公众号AppID" prop="mp_sub_appid">
            <el-input v-model="applyForm.mp_sub_appid" placeholder="请填写已认证的服务号或公众号AppID" />
          </el-form-item>
          <el-form-item label="公众号页面截图" prop="mp_pics">
            <div class="upload-wrapper">
              <el-upload :file-list="mpPicsFileList" :limit="5" accept="image/jpeg,image/png,image/bmp"
                :auto-upload="false" :on-change="(file) => handleImageChange(file, 'mpPics')"
                :on-remove="() => handleImageRemove('mpPics')" list-type="picture-card" multiple>
                <el-icon>
                  <Plus />
                </el-icon>
              </el-upload>
              <div class="upload-tip">最多上传5张，请提供展示商品/服务的页面截图</div>
            </div>
          </el-form-item>
        </template>

        <!-- ========== 4. 行业资质 ========== -->
        <el-divider content-position="left">行业资质</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <!-- ✅ 新增：行业选择 -->
            <el-form-item label="所属行业" prop="industry_category_id" required>
              <el-cascader v-model="selectedIndustry" :options="industryOptions" :props="{
                value: 'value',
                label: 'label',
                children: 'children',
                checkStrictly: false,
                emitPath: true
              }" placeholder="请选择所属行业" style="width: 100%" @change="handleIndustryChange" clearable filterable />
              <div class="form-tip" v-if="selectedIndustryInfo">
                结算费率：{{ selectedIndustryInfo.rate_desc }}，入账周期：{{ selectedIndustryInfo.billing_cycle }}
              </div>
            </el-form-item>

            <!-- ✅ 新增：特殊资质上传 -->
            <template v-if="needQualifications">
              <el-form-item label="特殊资质" prop="qualifications">
                <div class="upload-wrapper">
                  <el-upload :file-list="qualificationFileList" :limit="5" accept="image/jpeg,image/png,image/bmp"
                    :auto-upload="false" :on-change="(file) => handleImageChange(file, 'qualification')"
                    :on-remove="(file) => handleImageRemove('qualification', file)" list-type="picture-card" multiple>
                    <el-icon>
                      <Plus />
                    </el-icon>
                  </el-upload>
                  <div class="upload-tip">最多上传5张特殊资质照片</div>
                </div>
                <div class="form-tip" v-if="qualificationsGuide" style="white-space: pre-line;">{{ qualificationsGuide
                }}</div>
              </el-form-item>
            </template>
          </el-col>
        </el-row>


        <!-- 5. 结算账户 -->
        <el-divider content-position="left">结算账户</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="账户类型" prop="bank_account_type">
              <el-select v-model="applyForm.bank_account_type" placeholder="请选择账户类型" style="width: 100%"
                @change="onAccountTypeChange">
                <el-option label="对公银行账户" value="BANK_ACCOUNT_TYPE_CORPORATE" />
                <el-option label="个人银行卡" value="BANK_ACCOUNT_TYPE_PERSONAL" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="开户名称" prop="account_name">
              <el-input v-model="applyForm.account_name"
                :placeholder="applyForm.bank_account_type === 'BANK_ACCOUNT_TYPE_CORPORATE' ? '对公填公司全称' : '个人填法人姓名'" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="开户银行" prop="account_bank">
              <el-select v-model="applyForm.selected_bank_code" placeholder="请选择开户银行" filterable remote
                :remote-method="searchBanks" :loading="bankLoading" style="width: 100%" @change="onBankChange"
                @visible-change="onBankVisibleChange" clearable>
                <el-option v-for="bank in bankList" :key="bank.bank_alias_code" :label="bank.bank_alias"
                  :value="bank.bank_alias_code">
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span>{{ bank.bank_alias }}</span>
                    <el-tag v-if="bank.need_bank_branch" size="small" type="warning">需填支行</el-tag>
                    <el-tag v-else size="small" type="success">直连银行</el-tag>
                  </div>
                </el-option>
              </el-select>
              <div class="form-tip">支持搜索，绿色标签表示无需填写支行</div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="银行账号" prop="account_number">
              <el-input v-model="applyForm.account_number" placeholder="请输入银行账号" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 支行信息（仅当银行需要填写支行时显示） -->
        <template v-if="needBankBranch">
          <el-divider content-position="left">支行信息</el-divider>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="所在省份" prop="province_code" required>
                <el-select v-model="applyForm.province_code" placeholder="请选择省份" filterable :loading="provinceLoading"
                  style="width: 100%" @change="onProvinceChange" clearable>
                  <el-option v-for="province in provinceList" :key="province.province_code"
                    :label="province.province_name" :value="province.province_code">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="所在城市" prop="city_code" required>
                <el-select v-model="applyForm.city_code" placeholder="请先选择省份" filterable :loading="cityLoading"
                  :disabled="!applyForm.province_code" style="width: 100%" @change="onCityChange" clearable>
                  <el-option v-for="city in cityList" :key="city.city_code" :label="city.city_name"
                    :value="city.city_code">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="开户支行" prop="bank_branch_name" required>
                <el-select v-model="applyForm.bank_branch_name" placeholder="请选择开户支行" filterable remote
                  :remote-method="searchBranches" :loading="branchLoading" style="width: 100%" @change="onBranchChange"
                  clearable>
                  <el-option v-for="branch in branchList" :key="branch.bank_branch_id" :label="branch.bank_branch_name"
                    :value="branch.bank_branch_name">
                    <div>
                      <span>{{ branch.bank_branch_name }}</span>
                      <span style="float: right; color: #8492a6; font-size: 12px">
                        联行号: {{ branch.bank_branch_id }}
                      </span>
                    </div>
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="联行号" prop="bank_branch_id">
                <el-input v-model="applyForm.bank_branch_id" placeholder="选择支行后自动填充" disabled />
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <!-- ========== 6. 超级管理员 ========== -->
        <el-divider content-position="left">超级管理员（用于签约和管理）</el-divider>
        <el-form-item label="管理员类型" prop="contact_type">
          <el-radio-group v-model="applyForm.contact_type">
            <el-radio value="LEGAL">法定代表人/经营者</el-radio>
            <el-radio value="SUPER">经办人</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="管理员姓名" prop="contact_name">
              <el-input v-model="applyForm.contact_name" placeholder="通常为法人姓名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="管理员手机" prop="contact_mobile">
              <el-input v-model="applyForm.contact_mobile" placeholder="11位手机号" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="管理员邮箱" prop="contact_email">
              <el-input v-model="applyForm.contact_email" placeholder="用于接收重要通知" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 经办人证件信息（当选择经办人时显示） -->
        <template v-if="applyForm.contact_type === 'SUPER'">
          <el-form-item label="证件类型" prop="contact_id_doc_type">
            <el-select v-model="applyForm.contact_id_doc_type" placeholder="请选择证件类型" style="width: 100%">
              <el-option label="中国大陆居民-身份证" value="IDENTIFICATION_TYPE_IDCARD" />
            </el-select>
          </el-form-item>
          <el-form-item label="证件号码" prop="contact_id_number">
            <el-input v-model="applyForm.contact_id_number" placeholder="请输入身份证号码" />
          </el-form-item>
          <el-form-item label="证件照片（人像面）" prop="contact_id_doc_copy">
            <div class="upload-wrapper" v-if="!contactIdDocCopyFileList.length">
              <el-upload :file-list="contactIdDocCopyFileList" :limit="1" accept="image/jpeg,image/png,image/bmp"
                :auto-upload="false" :on-change="(file) => handleImageChange(file, 'contactIdDocCopy')"
                list-type="picture-card">
                <el-icon>
                  <Plus />
                </el-icon>
              </el-upload>
            </div>
            <div v-else class="uploaded-preview">
              <div class="preview-image">
                <img :src="contactIdDocCopyFileList[0].url" class="preview-img" />
                <div class="preview-actions">
                  <el-button type="danger" size="small" @click="handleImageRemove('contactIdDocCopy')">删除</el-button>
                  <el-button type="primary" size="small" @click="reUploadImage('contactIdDocCopy')">重新上传</el-button>
                </div>
              </div>
            </div>
          </el-form-item>

          <el-form-item label="证件照片（国徽面）" prop="contact_id_doc_copy_back">
            <div class="upload-wrapper" v-if="!contactIdDocCopyBackFileList.length">
              <el-upload :file-list="contactIdDocCopyBackFileList" :limit="1" accept="image/jpeg,image/png,image/bmp"
                :auto-upload="false" :on-change="(file) => handleImageChange(file, 'contactIdDocCopyBack')"
                list-type="picture-card">
                <el-icon>
                  <Plus />
                </el-icon>
              </el-upload>
            </div>
            <div v-else class="uploaded-preview">
              <div class="preview-image">
                <img :src="contactIdDocCopyBackFileList[0].url" class="preview-img" />
                <div class="preview-actions">
                  <el-button type="danger" size="small"
                    @click="handleImageRemove('contactIdDocCopyBack')">删除</el-button>
                  <el-button type="primary" size="small" @click="reUploadImage('contactIdDocCopyBack')">重新上传</el-button>
                </div>
              </div>
            </div>
          </el-form-item>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="证件有效期开始" prop="contact_period_begin">
                <el-date-picker v-model="applyForm.contact_period_begin" type="date" placeholder="开始日期"
                  value-format="YYYY-MM-DD" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="证件有效期结束" prop="contact_period_end">
                <el-date-picker v-model="applyForm.contact_period_end_date" type="date" placeholder="结束日期"
                  value-format="YYYY-MM-DD" style="width: 100%" :disabled="applyForm.contact_long_term" />
                <div style="margin-top: 8px;">
                  <el-checkbox v-model="applyForm.contact_long_term">长期有效</el-checkbox>
                </div>
                <div class="form-tip">勾选"长期有效"或选择具体日期</div>
              </el-form-item>
            </el-col>
          </el-row>
        </template>
      </el-form>
      <template #footer>
        <el-button @click="applyDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitApply" :loading="submittingApply">提交申请</el-button>
      </template>
    </el-dialog>

    <!-- 签约二维码弹窗 -->
    <el-dialog v-model="signQrDialogVisible" title="扫码签约" width="400px" center>
      <div style="text-align: center;">
        <div v-if="signQrUrl" class="qr-container">
          <img :src="signQrUrl" alt="签约二维码" class="qr-image" />
          <p class="qr-tip">请使用超级管理员的微信扫码完成签约</p>
          <el-button type="primary" link @click="copySignUrl" class="copy-link">
            复制签约链接
          </el-button>
        </div>
        <div v-else class="qr-loading">
          <el-icon class="is-loading">
            <Loading />
          </el-icon>
          <span>加载中...</span>
        </div>
      </div>
      <template #footer>
        <el-button @click="signQrDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElLoading, ElMessageBox } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { getUploadSignature, uploadCertToLocal } from '@/utils/uploadUtils'
import { CreditCard, Phone, Wallet, Edit, Upload, DocumentAdd, Plus, Refresh } from '@element-plus/icons-vue'



export default {
  name: 'SystemSettingPage',
  components: { CreditCard, Phone, Wallet, Edit, Upload, DocumentAdd, Plus, Refresh },
  setup() {
    const router = useRouter()

    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const storeId = ref(userInfo.id)
    const agentId = ref(userInfo.agent_id || 0)

    const saving = ref(false)
    const uploading = ref(false)
    const uploadProgress = ref(0)
    const uploadProgressStatus = ref('')
    const uploadLogs = ref([])

    const miniProgramConfig = ref({})
    const payConfig = ref({})

    const latestApply = ref(null)
    const querying = ref(false)
    const submittingApply = ref(false)
    const applyDialogVisible = ref(false)
    const applyFormRef = ref(null)


    // 省份城市相关
    const provinceList = ref([])
    const provinceLoading = ref(false)
    const cityList = ref([])
    const cityLoading = ref(false)

    // 银行相关
    const bankList = ref([])
    const bankLoading = ref(false)
    const needBankBranch = ref(false)
    const selectedBankInfo = ref(null)

    // 支行相关
    const branchList = ref([])
    const branchLoading = ref(false)

    // 在 setup() 中添加
    const signQrDialogVisible = ref(false)
    const signQrUrl = ref('')


    const applyForm = reactive({
      subject_type: 'SUBJECT_TYPE_INDIVIDUAL',
      merchant_name: '',
      merchant_shortname: '',
      license_number: '',
      legal_person: '',
      license_period_begin: '',
      license_period_end: '长期',
      license_period_end_date: '',      // 营业执照有效期结束日期
      license_long_term: true,          // 营业执照是否长期
      company_address: '',
      business_license_media_id: '',
      business_license_url: '',
      id_card_name: '',
      id_card_number: '',
      id_card_address: '',
      card_period_begin: '',
      card_period_end: '长期',
      card_period_end_date: '',         // 身份证有效期结束日期
      card_long_term: false,             // 身份证是否长期
      owner: true,
      id_card_front_media_id: '',
      id_card_front_url: '',
      id_card_back_media_id: '',
      id_card_back_url: '',
      service_phone: '',
      sales_scenes_type: 'SALES_SCENES_MINI_PROGRAM',
      biz_store_name: '',
      biz_store_address: '',
      store_entrance_pic: '',
      indoor_pic: '',
      biz_sub_appid: '',
      mini_program_sub_appid: '',
      mini_program_pics: '[]',
      mp_sub_appid: '',
      mp_pics: '[]',

      // 银行相关新增字段
      account_bank_code: '',      // 开户银行编码
      selected_bank_code: '',   // 存储选中的银行唯一编码（bank_alias_code）
      selected_bank_name: '',     // 用户选择的真实银行名称（用于回显）
      province_code: '',          // 省份编码  
      city_code: '',              // 城市编码
      bank_branch_name: '',       // 开户支行名称
      bank_branch_id: '',         // 联行号

      // ✅ 新增行业字段
      industry_category_id: 0,
      industry_category_name: '',
      settlement_rule_id: '719',
      qualification_type: '餐饮',
      qualifications: '[]',
      bank_account_type: 'BANK_ACCOUNT_TYPE_PERSONAL',
      account_name: '',
      account_bank: '',
      account_number: '',
      contact_type: 'LEGAL',
      contact_name: '',
      contact_mobile: '',
      contact_email: '',
      contact_id_number: '',
      contact_id_doc_type: 'IDENTIFICATION_TYPE_IDCARD',
      contact_id_doc_copy: '',
      contact_id_doc_copy_back: '',
      contact_period_begin: '',
      contact_period_end: '长期',
      contact_period_end_date: '',     // ✅ 新增
      contact_long_term: false          // ✅ 新增
    })

    const licenseFileList = ref([])
    const idCardFrontFileList = ref([])
    const idCardBackFileList = ref([])
    const storeEntranceFileList = ref([])
    const indoorFileList = ref([])
    const miniProgramPicsFileList = ref([])
    const mpPicsFileList = ref([])
    const contactIdDocCopyFileList = ref([])
    const contactIdDocCopyBackFileList = ref([])

    // ========== 行业选择相关 ==========
    const industryOptions = ref([])
    const selectedIndustry = ref([])
    const selectedIndustryInfo = ref(null)
    const needQualifications = ref(false)
    const qualificationsGuide = ref('')
    const qualificationFileList = ref([])

    const applyRules = {
      subject_type: [{ required: true, message: '请选择主体类型', trigger: 'change' }],
      merchant_name: [{ required: true, message: '请输入商户全称', trigger: 'blur' }],
      merchant_shortname: [{ required: true, message: '请输入商户简称', trigger: 'blur' }],
      license_number: [{ required: true, message: '请输入营业执照号', trigger: 'blur' }],
      legal_person: [{ required: true, message: '请输入法人姓名', trigger: 'blur' }],
      license_period_begin: [{ required: true, message: '请选择营业执照有效期开始', trigger: 'change' }],
      license_period_end: [{ required: true, message: '请输入营业执照有效期结束', trigger: 'blur' }],
      company_address: [{ required: true, message: '请输入营业执照地址', trigger: 'blur' }],
      business_license_media_id: [{ required: true, message: '请上传营业执照', trigger: 'change' }],
      id_card_name: [{ required: true, message: '请输入身份证姓名', trigger: 'blur' }],
      id_card_number: [{ required: true, message: '请输入身份证号码', trigger: 'blur' }],
      id_card_address: [{ required: true, message: '请输入身份证地址', trigger: 'blur' }],  // 新增
      card_period_begin: [{ required: true, message: '请选择身份证有效期开始', trigger: 'change' }],
      card_period_end: [{ required: true, message: '请输入身份证有效期结束', trigger: 'blur' }],
      service_phone: [{ required: true, message: '请输入客服电话', trigger: 'blur' }],
      sales_scenes_type: [{ required: true, message: '请选择经营场景', trigger: 'change' }],
      biz_store_name: [{ required: true, message: '请输入门店名称', trigger: 'blur' }],
      biz_store_address: [{ required: true, message: '请输入经营地址详情', trigger: 'blur' }],
      store_entrance_pic: [{ required: true, message: '请上传门店门头照片', trigger: 'change' }],
      indoor_pic: [{ required: true, message: '请上传门店内部照片', trigger: 'change' }],
      bank_account_type: [{ required: true, message: '请选择账户类型', trigger: 'change' }],
      account_name: [{ required: true, message: '请输入开户名称', trigger: 'blur' }],
      account_bank: [{ required: true, message: '请输入开户银行', trigger: 'blur' }],
      account_number: [{ required: true, message: '请输入银行账号', trigger: 'blur' }],
      contact_type: [{ required: true, message: '请选择管理员类型', trigger: 'change' }],
      contact_name: [{ required: true, message: '请输入管理员姓名', trigger: 'blur' }],
      industry_category_id: [{ required: true, message: '请选择所属行业', trigger: 'change' }],

      contact_mobile: [
        { required: true, message: '请输入管理员手机', trigger: 'blur' },
        { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
      ],
      contact_email: [{ required: true, message: '请输入管理员邮箱', trigger: 'blur' }],
      contact_id_doc_type: [
        {
          required: true,
          message: '请选择证件类型',
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (applyForm.contact_type === 'SUPER' && !value) {
              callback(new Error('请选择证件类型'));
            } else {
              callback();
            }
          }
        }
      ],
      contact_id_doc_copy: [
        {
          required: true,
          message: '请上传证件正面照片',
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (applyForm.contact_type === 'SUPER' && !value) {
              callback(new Error('请上传证件正面照片'));
            } else {
              callback();
            }
          }
        }
      ],
      contact_id_doc_copy_back: [
        {
          required: true,
          message: '请上传证件反面照片',
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (applyForm.contact_type === 'SUPER' && !value) {
              callback(new Error('请上传证件反面照片'));
            } else {
              callback();
            }
          }
        }
      ],
      contact_period_begin: [
        {
          required: true,
          message: '请选择证件有效期开始',
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (applyForm.contact_type === 'SUPER' && !value) {
              callback(new Error('请选择证件有效期开始'));
            } else {
              callback();
            }
          }
        }
      ],
      contact_period_end: [
        {
          required: true,
          message: '请输入证件有效期结束',
          trigger: 'blur',
          validator: (rule, value, callback) => {
            if (applyForm.contact_type === 'SUPER' && !value) {
              callback(new Error('请输入证件有效期结束'));
            } else {
              callback();
            }
          }
        }
      ]
    }

    const hasProcessingApply = computed(() => {
      // 只有审核中(1)状态才禁用申请按钮
      return latestApply.value && latestApply.value.status === 1
    })


    // 判断是否需要显示签约按钮（需要用户操作的状态）
    const shouldShowSignButton = (wxState) => {
      const needActionStates = [
        'APPLYMENT_STATE_AUDITING',           // 审核中 - 绑定微信接收通知
        'APPLYMENT_STATE_TO_BE_CONFIRMED',    // 待账户验证
        'APPLYMENT_STATE_TO_BE_SIGNED',       // 待签约
      ]
      return needActionStates.includes(wxState)
    }

    // 获取状态文本
    const getStatusText = (apply) => {
      if (!apply) return ''

      // 优先使用微信状态
      if (apply.wx_applyment_state) {
        const stateTextMap = {
          'APPLYMENT_STATE_EDITTING': '编辑中',
          'APPLYMENT_STATE_AUDITING': '审核中',
          'APPLYMENT_STATE_REJECTED': '审核驳回',
          'APPLYMENT_STATE_TO_BE_CONFIRMED': '待账户验证',
          'APPLYMENT_STATE_TO_BE_SIGNED': '待签约',
          'APPLYMENT_STATE_SIGNING': '开通权限中',
          'APPLYMENT_STATE_FINISHED': '已完成',
          'APPLYMENT_STATE_CANCELED': '已作废'
        }
        return stateTextMap[apply.wx_applyment_state] || '未知'
      }

      // 降级使用本地状态
      const statusMap = { 0: '草稿（待提交）', 1: '审核中', 2: '审核通过', 3: '审核驳回', 4: '已签约', 5: '已取消' }
      return statusMap[apply.status] || '未知'
    }

    const statusTitle = computed(() => {
      if (!latestApply.value) return ''
      let text = getStatusText(latestApply.value)
      // 如果有微信状态描述，附加显示
      if (latestApply.value.wx_applyment_state_msg) {
        text += `（${latestApply.value.wx_applyment_state_msg}）`
      }
      return `当前状态：${text}`
    })

    const statusType = computed(() => {
      if (!latestApply.value) return 'info'
      const wxState = latestApply.value.wx_applyment_state
      const status = latestApply.value.status

      if (wxState) {
        if (wxState === 'APPLYMENT_STATE_FINISHED') return 'success'
        if (wxState === 'APPLYMENT_STATE_REJECTED') return 'warning'
        if (wxState === 'APPLYMENT_STATE_CANCELED') return 'info'
        return 'info'
      }

      if (status === 2 || status === 4) return 'success'
      if (status === 3) return 'warning'
      return 'info'
    })

    const miniProgramDialogVisible = ref(false)
    const payDialogVisible = ref(false)
    const uploadCodeDialogVisible = ref(false)

    const miniProgramForm = reactive({
      appid: '',
      appsecret: '',
      upload_key: ''
    })

    const payForm = reactive({
      mchid: '',
      api_key: '',
      api_v3_key: '',
      pay_mode: 'sandbox'
    })

    const uploadForm = reactive({
      appid: '',
      privateKeyContent: '',
      api_url: config.BASE_URL,
      default_store_id: 2
    })

    const certFiles = ref([])
    const certData = ref({})


    /**
     * 重新上传图片
     */
    // eslint-disable-next-line no-unused-vars
    const reUploadImage = (type) => {
      // 清除原有的图片数据
      const typeMap = {
        license: {
          mediaField: 'business_license_media_id',
          urlField: 'business_license_url',
          fileList: licenseFileList
        },
        idCardFront: {
          mediaField: 'id_card_front_media_id',
          urlField: 'id_card_front_url',
          fileList: idCardFrontFileList
        },
        idCardBack: {
          mediaField: 'id_card_back_media_id',
          urlField: 'id_card_back_url',
          fileList: idCardBackFileList
        },
        contactIdDocCopy: {
          mediaField: 'contact_id_doc_copy',
          urlField: null,
          fileList: contactIdDocCopyFileList
        },
        contactIdDocCopyBack: {
          mediaField: 'contact_id_doc_copy_back',
          urlField: null,
          fileList: contactIdDocCopyBackFileList
        }
      }

      const config = typeMap[type]
      if (config) {
        // 清空数据
        applyForm[config.mediaField] = ''
        if (config.urlField) applyForm[config.urlField] = ''
        config.fileList.value = []
      }
    }

    // 复制签约链接
    const copySignUrl = async () => {
      try {
        await navigator.clipboard.writeText(signQrUrl.value)
        ElMessage.success('签约链接已复制，请在浏览器中打开')
      } catch (err) {
        ElMessage.error('复制失败，请手动复制')
      }
    }

    // 修改 queryApplyStatus 函数
    const queryApplyStatus = async () => {
      querying.value = true
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, {
          params: { action: 'query', store_id: storeId.value }
        })
        if (res.data.code === 200) {
          const wxResult = res.data.data?.wx_result
          const status = res.data.data?.status

          ElMessage.success('状态已更新')
          await fetchLatestApply()

          // 如果是待签约状态且有 sign_url，弹窗显示二维码
          if (status === 4 && wxResult?.sign_url) {  // 4 表示已签约状态
            signQrUrl.value = wxResult.sign_url
            signQrDialogVisible.value = true
          } else if (wxResult?.applyment_state === 'APPLYMENT_STATE_TO_BE_SIGNED' && wxResult?.sign_url) {
            // 或者根据微信返回的状态判断
            signQrUrl.value = wxResult.sign_url
            signQrDialogVisible.value = true
          }
        } else {
          ElMessage.error(res.data.message || '查询失败')
        }
      } catch (error) {
        console.error('查询失败:', error)
        ElMessage.error('查询失败')
      } finally {
        querying.value = false
      }
    }

    // 显示签约二维码弹窗
    const showSignQrCode = (url) => {
      if (url) {
        signQrUrl.value = url
        signQrDialogVisible.value = true
      }
    }

    // 获取行业列表（根据主体类型自动切换接口）
    const fetchIndustryList = async () => {
      try {
        // ✅ 关键：个体 / 企业 自动切换行业接口
        let apiUrl = ''
        if (applyForm.subject_type === 'SUBJECT_TYPE_INDIVIDUAL') {
          apiUrl = `${config.BASE_URL}/shop/api/industry_api.php`
        } else {
          apiUrl = `${config.BASE_URL}/shop/api/industry_api_qy.php`
        }

        const res = await axios.get(apiUrl, {
          params: { action: 'list' }
        })

        if (res.data.errorcode === 0) {
          // 转换数据格式为级联选择器需要的格式
          industryOptions.value = res.data.data.big_industry_list.map(item => ({
            value: item.big_industry_info.big_industry_id,
            label: item.big_industry_info.big_industry_name,
            children: item.industry_category_list.map(cat => ({
              value: cat.industry_category_info.category_id,
              label: cat.industry_category_info.category_name,
              // 保存完整信息供后续使用
              category_info: cat.industry_category_info,
              settlement_info: cat.settlement_rule_info
            }))
          }))
        }
      } catch (error) {
        console.error('获取行业列表失败:', error)
      }
    }

    // 行业选择变化
    const handleIndustryChange = (value) => {
      if (!value || value.length === 0) {
        selectedIndustryInfo.value = null
        needQualifications.value = false
        qualificationsGuide.value = ''
        return
      }

      // value 是选中的路径数组 [大行业ID, 子行业ID]
      const categoryId = value[value.length - 1]

      // 遍历找到选中的行业
      for (const big of industryOptions.value) {
        const category = big.children?.find(cat => cat.value === categoryId)
        if (category) {
          const catInfo = category.category_info
          const settlementInfo = category.settlement_info

          selectedIndustryInfo.value = {
            category_id: catInfo.category_id,
            category_name: catInfo.category_name,
            settlement_rule_id: settlementInfo.settlement_rule_id,
            rate_desc: settlementInfo.rate_desc,
            billing_cycle: settlementInfo.billing_cycle_desc,
            qualifications_required: catInfo.qualifications_type === 1,
            qualifications_guide: catInfo.qualifications_guide
          }

          needQualifications.value = catInfo.qualifications_type === 1
          qualificationsGuide.value = catInfo.qualifications_guide || ''

          // 保存到表单
          applyForm.industry_category_id = catInfo.category_id
          applyForm.industry_category_name = catInfo.category_name
          applyForm.settlement_rule_id = String(settlementInfo.settlement_rule_id)
          applyForm.qualification_type = catInfo.category_name

          break
        }
      }
    }

    const fetchConfig = async () => {
      const loading = ElLoading.service({ text: '加载中...' })
      try {
        const miniRes = await axios.get(`${config.BASE_URL}/shop/api/config_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, config_key: 'miniprogram' }
        })
        if (miniRes.data.code === 200 && miniRes.data.data) {
          miniProgramConfig.value = miniRes.data.data
          uploadForm.appid = miniProgramConfig.value.appid || ''
          uploadForm.privateKeyContent = miniProgramConfig.value.upload_key || ''
        }

        const payRes = await axios.get(`${config.BASE_URL}/shop/api/config_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, config_key: 'wechat_pay' }
        })
        if (payRes.data.code === 200 && payRes.data.data) {
          payConfig.value = payRes.data.data
          if (payConfig.value.cert_files) certData.value = payConfig.value.cert_files
        }

        await fetchLatestApply()

        uploadForm.default_store_id = storeId.value
        uploadForm.api_url = config.BASE_URL
      } catch (error) {
        console.error('获取配置失败:', error)
        ElMessage.error('获取配置失败')
      } finally {
        loading.close()
      }
    }

    const fetchLatestApply = async () => {
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, {
          params: { action: 'latest', store_id: storeId.value }
        })
        if (res.data.code === 200) {
          latestApply.value = res.data.data || null
        }
      } catch (error) {
        console.error('获取进件状态失败:', error)
      }
    }


    const openApplyDialog = async () => {
      resetApplyForm()

      // ✅ 加载行业列表
      if (industryOptions.value.length === 0) {
        await fetchIndustryList()
      }
      // 尝试加载草稿或驳回的申请
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, {
          params: { action: 'latest', store_id: storeId.value }
        })
        if (res.data.code === 200 && res.data.data) {
          const draft = res.data.data
          // 只有草稿(0)或驳回(3)状态才加载数据用于编辑
          if (draft.status === 0 || draft.status === 3) {
            // 加载数据到表单
            Object.keys(applyForm).forEach(key => {
              if (draft[key] !== undefined && draft[key] !== null && draft[key] !== '') {
                // 特殊处理 owner 字段：将 1/0 转换为 true/false
                if (key === 'owner') {
                  applyForm[key] = draft[key] == 1 ? true : false
                } else {
                  applyForm[key] = draft[key]
                }
              }
            })

            // ✅ 处理行业回显（使用名称）
            if (draft.qualification_type) {  // qualification_type 存储的是行业名称
              findIndustryPath(draft.qualification_type)
            }

            // 恢复门店门头照片
            if (draft.store_entrance_pic && draft.store_entrance_pic !== '') {
              try {
                let items = JSON.parse(draft.store_entrance_pic)
                if (!Array.isArray(items)) items = [items]
                const item = items[0] || {}
                storeEntranceFileList.value = [{
                  uid: Date.now(),
                  name: '门头照片',
                  url: item.url || '',   // ✅ 这里！和资质一样写法！
                  media_id: item.media_id || ''
                }]
                applyForm.store_entrance_pic = draft.store_entrance_pic
              } catch (e) {
                console.error('解析门头照片失败:', e)
              }
            }


            // 恢复门店内部照片
            if (draft.indoor_pic && draft.indoor_pic !== '') {
              try {
                let items = JSON.parse(draft.indoor_pic)
                if (!Array.isArray(items)) items = [items]
                const item = items[0] || {}
                indoorFileList.value = [{
                  uid: Date.now(),
                  name: '内部照片',
                  url: item.url || '',   // ✅ 统一写法
                  media_id: item.media_id || ''
                }]
                applyForm.indoor_pic = draft.indoor_pic
              } catch (e) {
                console.error('解析内部照片失败:', e)
              }
            }

            // 恢复经办人证件正面
            if (draft.contact_id_doc_copy && draft.contact_id_doc_copy !== '') {
              try {
                let items = JSON.parse(draft.contact_id_doc_copy)
                if (!Array.isArray(items)) items = [items]
                const item = items[0] || {}
                contactIdDocCopyFileList.value = [{
                  uid: Date.now(),
                  name: '证件正面',
                  url: item.url || '',
                  media_id: item.media_id || ''
                }]
                applyForm.contact_id_doc_copy = draft.contact_id_doc_copy
              } catch (e) {
                console.error('解析经办人证件正面失败:', e)
              }
            }

            // 恢复经办人证件反面
            if (draft.contact_id_doc_copy_back && draft.contact_id_doc_copy_back !== '') {
              try {
                let items = JSON.parse(draft.contact_id_doc_copy_back)
                if (!Array.isArray(items)) items = [items]
                const item = items[0] || {}
                contactIdDocCopyBackFileList.value = [{
                  uid: Date.now(),
                  name: '证件反面',
                  url: item.url || '',
                  media_id: item.media_id || ''
                }]
                applyForm.contact_id_doc_copy_back = draft.contact_id_doc_copy_back
              } catch (e) {
                console.error('解析经办人证件反面失败:', e)
              }
            }


            // 恢复小程序截图
            if (draft.mini_program_pics && draft.mini_program_pics !== '[]') {
              try {
                const items = JSON.parse(draft.mini_program_pics)
                miniProgramPicsFileList.value = items.map((item, index) => ({
                  uid: Date.now() + index,
                  name: `截图${index + 1}`,
                  url: item.url || '',
                  media_id: item.media_id || ''
                }))
              } catch (e) {
                console.error('解析小程序截图失败:', e)
              }
            }


            // 恢复公众号截图
            if (draft.mp_pics && draft.mp_pics !== '[]') {
              try {
                const items = JSON.parse(draft.mp_pics)
                mpPicsFileList.value = items.map((item, index) => ({
                  uid: Date.now() + index,
                  name: `截图${index + 1}`,
                  url: item.url || '',
                  media_id: item.media_id || ''
                }))
              } catch (e) {
                console.error('解析公众号截图失败:', e)
              }
            }


            // ✅ 处理特殊资质回显
            if (draft.qualifications && draft.qualifications !== '[]') {
              try {
                const items = JSON.parse(draft.qualifications)

                // 判断是新格式（对象数组）还是旧格式（字符串数组）
                if (items.length > 0 && typeof items[0] === 'object' && items[0].media_id) {
                  // 新格式：{ media_id, url }
                  qualificationFileList.value = items.map((item, index) => ({
                    uid: Date.now() + index,
                    name: `资质${index + 1}`,
                    url: item.url || '',
                    media_id: item.media_id
                  }))
                } else {
                  // 旧格式：纯 media_id 数组（兼容）
                  qualificationFileList.value = items.map((mediaId, index) => ({
                    uid: Date.now() + index,
                    name: `资质${index + 1}`,
                    media_id: mediaId,
                    url: ''
                  }))
                }
              } catch (e) {
                console.error('解析特殊资质失败:', e)
              }
            }

            // 处理营业执照有效期回显
            if (draft.license_period_end === '长期') {
              applyForm.license_long_term = true
              applyForm.license_period_end_date = ''
            } else {
              applyForm.license_long_term = false
              applyForm.license_period_end_date = draft.license_period_end || ''
            }

            // 处理身份证有效期回显
            if (draft.card_period_end === '长期') {
              applyForm.card_long_term = true
              applyForm.card_period_end_date = ''
            } else {
              applyForm.card_long_term = false
              applyForm.card_period_end_date = draft.card_period_end || ''
            }

            // 处理经办人证件有效期回显
            if (draft.contact_period_end === '长期') {
              applyForm.contact_long_term = true
              applyForm.contact_period_end_date = ''
            } else if (draft.contact_period_end) {
              applyForm.contact_long_term = false
              applyForm.contact_period_end_date = draft.contact_period_end
            }

            // 恢复图片文件列表
            if (draft.business_license_url) {
              licenseFileList.value = [{ uid: Date.now(), name: '营业执照', url: draft.business_license_url }]
            }
            if (draft.id_card_front_url) {
              idCardFrontFileList.value = [{ uid: Date.now(), name: '身份证正面', url: draft.id_card_front_url }]
            }
            if (draft.id_card_back_url) {
              idCardBackFileList.value = [{ uid: Date.now(), name: '身份证反面', url: draft.id_card_back_url }]
            }

            // 恢复真实银行名称（用于回显）
            if (draft.selected_bank_name) {
              // 确保银行列表已加载
              if (bankList.value.length === 0) {
                await loadBankList('')  // 使用非防抖版本
              }

              // 查找匹配的银行
              const matched = bankList.value.find(b => b.bank_alias === draft.selected_bank_name)
              if (matched) {
                selectedBankInfo.value = matched
                needBankBranch.value = matched.need_bank_branch === true

                // 恢复银行编码
                applyForm.account_bank_code = matched.account_bank_code
                applyForm.selected_bank_name = draft.selected_bank_name
                applyForm.selected_bank_code = matched.bank_alias_code  // 设置唯一编码


                // 根据是否需要支行，设置 account_bank 显示值
                if (needBankBranch.value) {
                  applyForm.account_bank = '其他银行'
                } else {
                  applyForm.account_bank = matched.account_bank
                }

                // 如果需要支行，恢复支行信息
                if (needBankBranch.value && draft.bank_name) {
                  applyForm.bank_branch_name = draft.bank_name
                  applyForm.bank_branch_id = draft.bank_branch_id || ''

                  // ✅ 修改：使用 province_code 和 city_code，而不是 bank_address_code
                  if (draft.province_code) {
                    // 确保省份编码是数字
                    applyForm.province_code = parseInt(draft.province_code)

                    // 先确保省份列表已加载
                    if (provinceList.value.length === 0) {
                      await fetchProvinces()
                    }

                    // 触发省份变化，加载城市列表
                    await onProvinceChange(applyForm.province_code)

                    if (draft.city_code) {
                      await nextTick()
                      const cityCodeNum = parseInt(draft.city_code)
                      const cityExists = cityList.value.some(c => c.city_code === cityCodeNum)
                      if (cityExists) {
                        applyForm.city_code = cityCodeNum
                        // 触发城市变化，加载支行列表
                        await onCityChange(applyForm.city_code)

                        // 等待支行列表加载后，设置选中的支行
                        await nextTick()
                        const branchMatched = branchList.value.find(b => b.bank_branch_name === draft.bank_name)
                        if (branchMatched) {
                          applyForm.bank_branch_name = branchMatched.bank_branch_name
                          applyForm.bank_branch_id = branchMatched.bank_branch_id
                        }
                      }
                    }
                  }
                }
              }
            } else if (draft.account_bank && !draft.selected_bank_name) {
              // 兼容旧数据：没有 selected_bank_name 的情况
              if (bankList.value.length === 0) {
                await searchBanks('')
              }
              applyForm.account_bank = draft.account_bank
              const matched = bankList.value.find(b => b.account_bank === draft.account_bank)
              if (matched) {
                selectedBankInfo.value = matched
                needBankBranch.value = matched.need_bank_branch === true
                applyForm.selected_bank_name = matched.bank_alias
                applyForm.account_bank_code = matched.account_bank_code
              }
            }

            if (draft.status === 0) {
              ElMessage.info('已加载上次保存的草稿，可继续编辑')
            } else if (draft.status === 3) {
              ElMessage.warning('上次申请被驳回，请修改后重新提交')
            }
          }
        }
      } catch (error) {
        console.error('加载数据失败:', error)
      }

      // ✅ 去掉自动填充 AppID 的代码

      applyDialogVisible.value = true
    }

    // 监听主体类型变化（仅在弹窗打开时生效）
    watch(
      () => applyForm.subject_type,
      async (newVal, oldVal) => {
        // 只在弹窗打开、且值真正变化时执行
        if (applyDialogVisible.value && oldVal && newVal !== oldVal) {
          // 不清空 selectedIndustry，只是重新加载行业列表
          // 但需要提示用户重新选择
          await fetchIndustryList()

          // 清空行业相关信息（让用户重新选择）
          selectedIndustry.value = []
          selectedIndustryInfo.value = null
          needQualifications.value = false
          qualificationsGuide.value = ''

          // 清空表单中的行业字段
          applyForm.industry_category_id = 0
          applyForm.industry_category_name = ''
          applyForm.settlement_rule_id = '719'
          applyForm.qualification_type = ''

          ElMessage.info('主体类型已切换，请重新选择所属行业')
        }
      }
    )


    // 根据行业名称找到级联路径
    const findIndustryPath = (categoryName) => {
      if (!categoryName) return

      for (const big of industryOptions.value) {
        const category = big.children?.find(cat => cat.label === categoryName)
        if (category) {
          selectedIndustry.value = [big.value, category.value]
          handleIndustryChange(selectedIndustry.value)
          break
        }
      }
    }

    /**
 * 获取省份列表
 */
    const fetchProvinces = async () => {
      provinceLoading.value = true
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/bank_api.php`, {
          params: {
            action: 'provinces',
            agent_id: agentId.value
          }
        })
        if (res.data.code === 200) {
          provinceList.value = res.data.data || []
        }
      } catch (error) {
        console.error('获取省份列表失败:', error)
      } finally {
        provinceLoading.value = false
      }
    }

    /**
     * 省份变化，获取城市列表
     */
    const onProvinceChange = async (provinceCode) => {
      applyForm.city_code = ''
      applyForm.bank_branch_name = ''
      applyForm.bank_branch_id = ''
      cityList.value = []
      branchList.value = []

      if (!provinceCode) return

      cityLoading.value = true
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/bank_api.php`, {
          params: {
            action: 'cities',
            agent_id: agentId.value,
            province_code: provinceCode
          }
        })
        if (res.data.code === 200) {
          cityList.value = res.data.data || []
        }
      } catch (error) {
        console.error('获取城市列表失败:', error)
        ElMessage.error('获取城市列表失败')
      } finally {
        cityLoading.value = false
      }
    }

    /**
     * 城市变化，加载支行列表
     */
    const onCityChange = async (cityCode) => {
      applyForm.bank_branch_name = ''
      applyForm.bank_branch_id = ''
      branchList.value = []

      if (!cityCode || !selectedBankInfo.value) return

      await loadBranches('')
    }

    /**
     * 加载支行列表
     */
    const loadBranches = async (keyword = '', offset = 0) => {
      if (!selectedBankInfo.value || !applyForm.city_code) return

      branchLoading.value = true
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/bank_api.php`, {
          params: {
            action: 'branches',
            agent_id: agentId.value,
            bank_alias_code: selectedBankInfo.value.bank_alias_code,
            city_code: applyForm.city_code,
            offset: offset,
            limit: 100,
            keyword: keyword  // 添加 keyword 参数
          }
        })
        if (res.data.code === 200) {
          const branchData = res.data.data
          branchList.value = branchData.data || []

          // 保存银行信息
          if (branchData.account_bank) {
            applyForm.account_bank = branchData.account_bank
            applyForm.account_bank_code = branchData.account_bank_code
          }
        }
      } catch (error) {
        console.error('获取支行列表失败:', error)
        ElMessage.error('获取支行列表失败')
      } finally {
        branchLoading.value = false
      }
    }

    /**
     * 搜索支行（远程搜索）
     */
    const searchBranches = async (keyword) => {
      if (!selectedBankInfo.value || !applyForm.city_code) {
        if (keyword) {
          ElMessage.warning('请先选择省份和城市')
        }
        return
      }
      await loadBranches(keyword)
    }

    /**
     * 选择支行
     */
    const onBranchChange = (branchName) => {
      const selected = branchList.value.find(b => b.bank_branch_name === branchName)
      if (selected) {
        applyForm.bank_branch_id = selected.bank_branch_id
      }
    }

    /**
     * 账户类型变化
     */
    const onAccountTypeChange = () => {
      applyForm.account_bank = ''
      applyForm.account_bank_code = ''
      applyForm.province_code = ''
      applyForm.city_code = ''
      applyForm.bank_branch_name = ''
      applyForm.bank_branch_id = ''
      bankList.value = []
      needBankBranch.value = false
      selectedBankInfo.value = null
      cityList.value = []
      branchList.value = []

      searchBanks('')
    }

    // 下拉框显示/隐藏时的回调
    const onBankVisibleChange = (visible) => {
      if (!visible) {
        // 下拉框关闭时，如果当前选中的值不在列表中，重新匹配
        if (applyForm.account_bank && bankList.value.length > 0) {
          const exists = bankList.value.some(b => b.account_bank === applyForm.account_bank)
          if (!exists && applyForm.selected_bank_name) {
            // 如果不在列表中，尝试重新搜索
            searchBanks(applyForm.selected_bank_name)
          }
        }
      }
    }


    // 防抖函数
    const debounce = (fn, delay) => {
      let timer = null
      return function (...args) {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => {
          fn.apply(this, args)
        }, delay)
      }
    }

    // 加载银行列表（非防抖，用于初始化和回显）
    const loadBankList = async (keyword = '') => {
      bankLoading.value = true
      try {
        const accountType = applyForm.bank_account_type === 'BANK_ACCOUNT_TYPE_CORPORATE' ? 'corporate' : 'personal'
        const res = await axios.get(`${config.BASE_URL}/shop/api/bank_api.php`, {
          params: {
            action: 'banks',
            agent_id: agentId.value,
            account_type: accountType,
            keyword: keyword,
            offset: 0,
            limit: 200
          }
        })

        if (res.data.code === 200) {
          bankList.value = res.data.data.data || []
        }
      } catch (error) {
        console.error('获取银行列表失败:', error)
        ElMessage.error('获取银行列表失败')
      } finally {
        bankLoading.value = false
      }
    }

    // 搜索银行（使用防抖，用于用户输入）
    const searchBanks = debounce(async (keyword) => {
      await loadBankList(keyword)
    }, 300)


    const onBankChange = (bankAliasCode) => {
      if (!bankAliasCode) return
      const selected = bankList.value.find(b => b.bank_alias_code === bankAliasCode)
      if (selected) {
        selectedBankInfo.value = selected
        needBankBranch.value = selected.need_bank_branch === true

        // 保存银行编码
        applyForm.account_bank_code = selected.account_bank_code
        // 保存用户选择的真实银行名称（用于回显）
        applyForm.selected_bank_name = selected.bank_alias
        // 保存选中的唯一编码
        applyForm.selected_bank_code = bankAliasCode

        // 清空支行相关字段
        applyForm.province_code = ''
        applyForm.city_code = ''
        applyForm.bank_branch_name = ''
        applyForm.bank_branch_id = ''
        cityList.value = []
        branchList.value = []

        if (!needBankBranch.value) {
          // 直连银行：account_bank 直接用银行名称
          applyForm.account_bank = selected.account_bank
          ElMessage.success(`已选择 ${selected.bank_alias}，无需填写支行信息`)
        } else {
          // 需要支行：account_bank 必须设置为 "其他银行"（微信接口要求）
          applyForm.account_bank = '其他银行'
          ElMessage.info('请选择省份和城市，然后选择支行')
          fetchProvinces()
        }
      }
    }

    const resetApplyForm = () => {
      Object.assign(applyForm, {
        subject_type: 'SUBJECT_TYPE_INDIVIDUAL',        // ✅ 默认个体工商户
        merchant_name: '',
        merchant_shortname: '',
        license_number: '',
        legal_person: '',
        license_period_begin: '',
        license_period_end: '长期',
        license_period_end_date: '',                     // ✅ 新增：日期值
        license_long_term: true,                         // ✅ 新增：是否长期
        company_address: '',
        business_license_media_id: '',
        business_license_url: '',
        id_card_name: '',
        id_card_number: '',
        id_card_address: '',
        card_period_begin: '',
        card_period_end: '长期',
        card_period_end_date: '',                        // ✅ 新增：日期值
        card_long_term: false,                            // ✅ 新增：是否长期
        owner: true,
        id_card_front_media_id: '',
        id_card_front_url: '',
        id_card_back_media_id: '',
        id_card_back_url: '',
        service_phone: '',
        sales_scenes_type: 'SALES_SCENES_MINI_PROGRAM',  // ✅ 默认小程序
        biz_store_name: '',
        biz_store_address: '',
        store_entrance_pic: '',
        indoor_pic: '',
        biz_sub_appid: '',
        mini_program_sub_appid: '',
        mini_program_pics: '[]',
        mp_sub_appid: '',
        mp_pics: '[]',

        // 添加新字段的重置
        account_bank_code: '',
        selected_bank_name: '',     // 添加这行
        selected_bank_code: '',
        province_code: '',
        city_code: '',
        bank_branch_name: '',
        bank_branch_id: '',

        // ✅ 新增
        industry_category_id: 0,
        industry_category_name: '',
        settlement_rule_id: '719',
        qualification_type: '餐饮',
        qualifications: '[]',
        bank_account_type: 'BANK_ACCOUNT_TYPE_PERSONAL', // ✅ 默认个人银行卡
        account_name: '',
        account_bank: '',
        account_number: '',
        contact_type: 'LEGAL',
        contact_name: '',
        contact_mobile: '',
        contact_email: '',
        contact_id_number: '',
        contact_id_doc_type: 'IDENTIFICATION_TYPE_IDCARD',
        contact_id_doc_copy: '',
        contact_id_doc_copy_back: '',
        contact_period_begin: '',
        contact_period_end: '长期',
        contact_period_end_date: '',      // ✅ 新增
        contact_long_term: false           // ✅ 新增
      })
      licenseFileList.value = []
      idCardFrontFileList.value = []
      idCardBackFileList.value = []
      storeEntranceFileList.value = []
      indoorFileList.value = []
      miniProgramPicsFileList.value = []
      mpPicsFileList.value = []
      contactIdDocCopyFileList.value = []
      contactIdDocCopyBackFileList.value = []
      qualificationFileList.value = []  // ✅ 新增
      selectedIndustry.value = []
      selectedIndustryInfo.value = null
      needQualifications.value = false
      // 添加新列表的重置
      bankList.value = []
      provinceList.value = []
      cityList.value = []
      branchList.value = []
      needBankBranch.value = false
      selectedBankInfo.value = null
      applyFormRef.value?.clearValidate()
    }

    const handleImageChange = async (file, type) => {
      if (!file.raw) return
      const rawFile = file.raw

      const validTypes = ['image/jpeg', 'image/png', 'image/bmp']
      if (!validTypes.includes(rawFile.type)) {
        ElMessage.error('只支持 JPG、PNG、BMP 格式')
        return false
      }
      if (rawFile.size > 5 * 1024 * 1024) {
        ElMessage.error('图片大小不能超过5MB')
        return false
      }

      const formData = new FormData()
      formData.append('file', rawFile)
      formData.append('action', 'upload_image')
      formData.append('store_id', storeId.value)
      formData.append('agent_id', agentId.value)

      const loading = ElLoading.service({ text: '上传中...' })
      try {
        const res = await axios.post(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (res.data.code === 200 && res.data.data?.media_id) {
          const mediaId = res.data.data.media_id
          const previewUrl = res.data.data.preview_url || '' // ✅ 后端返回的真实地址

          const typeMap = {
            license: { mediaField: 'business_license_media_id', urlField: 'business_license_url', fileList: licenseFileList },
            idCardFront: { mediaField: 'id_card_front_media_id', urlField: 'id_card_front_url', fileList: idCardFrontFileList },
            idCardBack: { mediaField: 'id_card_back_media_id', urlField: 'id_card_back_url', fileList: idCardBackFileList },

            // ✅ 修复：门头 + 内部 + 经办人证件，全部存储 {media_id, url}
            storeEntrance: { mediaField: 'store_entrance_pic', fileList: storeEntranceFileList, storeBoth: true },
            indoor: { mediaField: 'indoor_pic', fileList: indoorFileList, storeBoth: true },
            contactIdDocCopy: { mediaField: 'contact_id_doc_copy', fileList: contactIdDocCopyFileList, storeBoth: true },
            contactIdDocCopyBack: { mediaField: 'contact_id_doc_copy_back', fileList: contactIdDocCopyBackFileList, storeBoth: true },

            miniProgramPics: { mediaField: 'mini_program_pics', fileList: miniProgramPicsFileList, isArray: true, storeBoth: true },
            mpPics: { mediaField: 'mp_pics', fileList: mpPicsFileList, isArray: true, storeBoth: true },
            qualification: { mediaField: 'qualifications', fileList: qualificationFileList, isArray: true, storeBoth: true }
          }

          const config = typeMap[type]
          if (config) {
            if (config.isArray) {
              let items = []
              try { items = JSON.parse(applyForm[config.mediaField] || '[]') } catch (e) { items = [] }

              // ✅ 永远存储 {media_id, url}
              items.push({ media_id: mediaId, url: previewUrl })
              applyForm[config.mediaField] = JSON.stringify(items)
              config.fileList.value.push({ uid: Date.now(), name: rawFile.name, url: previewUrl })
            } else {
              // ✅ 单图：统一存储 {media_id, url}
              applyForm[config.mediaField] = JSON.stringify([{ media_id: mediaId, url: previewUrl }])
              if (config.urlField) applyForm[config.urlField] = previewUrl
              config.fileList.value = [{ uid: Date.now(), name: rawFile.name, url: previewUrl }]
            }
          }
          ElMessage.success('上传成功')
        } else {
          ElMessage.error(res.data.message || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        ElMessage.error('上传失败')
      } finally {
        loading.close()
      }
    }

    const handleImageRemove = (type, file) => {
      const typeMap = {
        license: { mediaField: 'business_license_media_id', urlField: 'business_license_url', fileList: licenseFileList },
        idCardFront: { mediaField: 'id_card_front_media_id', urlField: 'id_card_front_url', fileList: idCardFrontFileList },
        idCardBack: { mediaField: 'id_card_back_media_id', urlField: 'id_card_back_url', fileList: idCardBackFileList },
        storeEntrance: { mediaField: 'store_entrance_pic', fileList: storeEntranceFileList, storeBoth: true },
        indoor: { mediaField: 'indoor_pic', fileList: indoorFileList, storeBoth: true },
        contactIdDocCopy: { mediaField: 'contact_id_doc_copy', fileList: contactIdDocCopyFileList, storeBoth: true },
        contactIdDocCopyBack: { mediaField: 'contact_id_doc_copy_back', fileList: contactIdDocCopyBackFileList, storeBoth: true },
        miniProgramPics: { mediaField: 'mini_program_pics', fileList: miniProgramPicsFileList, isArray: true, storeBoth: true },
        mpPics: { mediaField: 'mp_pics', fileList: mpPicsFileList, isArray: true, storeBoth: true },
        qualification: { mediaField: 'qualifications', fileList: qualificationFileList, isArray: true, storeBoth: true }
      }

      const config = typeMap[type]
      if (!config) {
        console.error('未知的图片类型:', type)
        return
      }

      // 获取要删除的文件的 uid
      const fileUid = file?.uid
      if (!fileUid) {
        console.error('无法获取文件 uid:', file)
        return
      }

      if (config.isArray) {
        // 数组类型（多图）
        let items = []
        try {
          items = JSON.parse(applyForm[config.mediaField] || '[]')
        } catch (e) {
          items = []
        }

        // 找到要删除的索引
        const index = config.fileList.value.findIndex(f => f.uid === fileUid)
        if (index !== -1) {
          items.splice(index, 1)
          applyForm[config.mediaField] = JSON.stringify(items)
          config.fileList.value.splice(index, 1)
        }
      } else if (config.storeBoth) {
        // 单图片存储对象格式
        applyForm[config.mediaField] = ''
        config.fileList.value = []
      } else {
        // 普通单图片
        applyForm[config.mediaField] = ''
        if (config.urlField) applyForm[config.urlField] = ''
        config.fileList.value = []
      }
    }
    const submitApply = async () => {
      if (!applyFormRef.value) return

      // 添加支行信息验证
      if (needBankBranch.value) {
        if (!applyForm.province_code) {
          ElMessage.warning('请选择省份')
          return
        }
        if (!applyForm.city_code) {
          ElMessage.warning('请选择城市')
          return
        }
        if (!applyForm.bank_branch_name) {
          ElMessage.warning('请选择开户支行')
          return
        }
        if (!applyForm.bank_branch_id) {
          ElMessage.warning('请选择开户支行')
          return
        }
      }

      await applyFormRef.value.validate(async (valid) => {
        if (!valid) return

        if (!applyForm.business_license_media_id) {
          ElMessage.warning('请上传营业执照')
          return
        }
        if (!applyForm.id_card_front_media_id || !applyForm.id_card_back_media_id) {
          ElMessage.warning('请上传身份证正反面')
          return
        }

        submittingApply.value = true

        try {
          // 处理有效期字段
          const submitData = { ...applyForm }

          // 营业执照有效期
          if (applyForm.license_long_term) {
            submitData.license_period_end = '长期'
          } else {
            submitData.license_period_end = applyForm.license_period_end_date || ''
          }

          // 身份证有效期
          if (applyForm.card_long_term) {
            submitData.card_period_end = '长期'
          } else {
            submitData.card_period_end = applyForm.card_period_end_date || ''
          }

          // ✅ 经办人证件有效期
          if (applyForm.contact_long_term) {
            submitData.contact_period_end = '长期'
          } else {
            submitData.contact_period_end = applyForm.contact_period_end_date || ''
          }

          // ========== ✅ 终极修复：所有图片统一格式 = [{media_id,url}] ==========
          const formatImageField = (val) => {
            if (!val) return ''
            try {
              // 已经是对象/数组，直接返回
              if (typeof val === 'object') {
                return JSON.stringify(val)
              }
              // 字符串：尝试解析
              const parsed = JSON.parse(val)
              return JSON.stringify(parsed)
            } catch (e) {
              // 纯 media_id，包装成标准格式
              return JSON.stringify([{ media_id: val, url: '' }])
            }
          }

          // 统一格式化所有图片字段
          submitData.store_entrance_pic = formatImageField(applyForm.store_entrance_pic)
          submitData.indoor_pic = formatImageField(applyForm.indoor_pic)
          submitData.contact_id_doc_copy = formatImageField(applyForm.contact_id_doc_copy)
          submitData.contact_id_doc_copy_back = formatImageField(applyForm.contact_id_doc_copy_back)
          submitData.mini_program_pics = formatImageField(applyForm.mini_program_pics)
          submitData.mp_pics = formatImageField(applyForm.mp_pics)
          submitData.qualifications = formatImageField(applyForm.qualifications)

          // 打印最终提交结果（保留）
          console.log('【最终提交格式】', {
            store_entrance_pic: submitData.store_entrance_pic,
            indoor_pic: submitData.indoor_pic,
            mini_program_pics: submitData.mini_program_pics,
            mp_pics: submitData.mp_pics,
            contact_id_doc_copy: submitData.contact_id_doc_copy,
            contact_id_doc_copy_back: submitData.contact_id_doc_copy_back,
            qualifications: submitData.qualifications
          })

          // 第一步：保存草稿
          const draftPayload = {
            action: 'save_draft',
            store_id: storeId.value,
            agent_id: agentId.value,
            ...submitData
          }

          const draftRes = await axios.post(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, draftPayload)

          if (draftRes.data.code !== 200) {
            ElMessage.error(draftRes.data.message || '保存草稿失败')
            return
          }

          const draftId = draftRes.data.id
          ElMessage.success('草稿已保存，正在提交申请...')

          // 第二步：提交申请
          const submitPayload = {
            action: 'submit',
            store_id: storeId.value,
            agent_id: agentId.value,
            draft_id: draftId,
            ...submitData
          }

          const submitRes = await axios.post(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, submitPayload)

          if (submitRes.data.code === 200) {
            ElMessage.success('申请已提交，请等待审核')
            applyDialogVisible.value = false
            await fetchLatestApply()
          } else {
            ElMessage.warning(submitRes.data.message || '提交失败，草稿已保存，可稍后重试')
            applyDialogVisible.value = false
            await fetchLatestApply()
          }
        } catch (error) {
          console.error('提交失败:', error)
          ElMessage.error('提交失败，但草稿已保存')
          await fetchLatestApply()
        } finally {
          submittingApply.value = false
        }
      })
    }

    const cancelApply = async () => {
      if (!latestApply.value) return
      try {
        await ElMessageBox.confirm('确定取消当前申请吗？', '提示', { type: 'warning' })
        const res = await axios.post(`${config.BASE_URL}/shop/api/merchant_applyment_api.php`, {
          action: 'cancel',
          store_id: storeId.value,
          id: latestApply.value.id
        })
        if (res.data.code === 200) {
          ElMessage.success('已取消')
          await fetchLatestApply()
        } else {
          ElMessage.error(res.data.message || '取消失败')
        }
      } catch (error) {
        if (error !== 'cancel') console.error(error)
      }
    }

    const resetUploadForm = () => {
      uploadProgress.value = 0
      uploadProgressStatus.value = ''
      uploadLogs.value = []
    }

    const openUploadCodeDialog = () => {
      uploadForm.appid = miniProgramConfig.value.appid || ''
      uploadForm.privateKeyContent = miniProgramConfig.value.upload_key || ''
      uploadForm.default_store_id = storeId.value
      uploadForm.api_url = config.BASE_URL
      uploadLogs.value = []
      uploadProgress.value = 0
      uploadProgressStatus.value = ''

      if (!uploadForm.appid) {
        ElMessage.warning('请先配置小程序 AppID')
        return
      }
      if (!uploadForm.privateKeyContent) {
        ElMessage.warning('请先配置小程序上传密钥')
        return
      }

      uploadCodeDialogVisible.value = true
    }

    const submitUploadCode = async () => {
      if (!uploadForm.appid) { ElMessage.warning('请先配置小程序AppID'); return }
      if (!uploadForm.privateKeyContent) { ElMessage.warning('请先配置小程序上传密钥'); return }
      if (!uploadForm.api_url) { ElMessage.warning('API域名未配置'); return }
      if (!uploadForm.default_store_id) { ElMessage.warning('商家ID未配置'); return }

      uploading.value = true
      uploadProgress.value = 10
      uploadProgressStatus.value = ''
      uploadLogs.value = ['开始上传小程序代码...']

      try {
        const response = await axios.post(
          'https://db.juzhendi.com/auth/minidb/group_buy_mini/upload.php',
          {
            appid: uploadForm.appid,
            privateKeyContent: uploadForm.privateKeyContent,
            api_url: uploadForm.api_url,
            default_store_id: String(uploadForm.default_store_id)
          },
          {
            timeout: 120000,
            onUploadProgress: (progressEvent) => {
              const percent = Math.min(90, 10 + Math.floor((progressEvent.loaded || 0) / (progressEvent.total || 1) * 80))
              uploadProgress.value = percent
            }
          }
        )

        uploadProgress.value = 100

        if (response.data.status === 'success') {
          uploadProgressStatus.value = 'success'
          uploadLogs.value.push('✅ 上传成功！')
          if (response.data.detail && Array.isArray(response.data.detail)) {
            response.data.detail.forEach(line => { if (line && line.trim()) uploadLogs.value.push(line) })
          }
          ElMessage.success('小程序代码上传成功')
        } else {
          uploadProgressStatus.value = 'exception'
          uploadLogs.value.push(`❌ 上传失败: ${response.data.message}`)
          ElMessage.error(response.data.message || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        uploadProgressStatus.value = 'exception'
        uploadLogs.value.push(`❌ 请求失败: ${error.message}`)
        if (error.response && error.response.data) {
          uploadLogs.value.push(`错误详情: ${JSON.stringify(error.response.data)}`)
        }
        ElMessage.error('上传失败，请检查网络或稍后重试')
      } finally {
        uploading.value = false
      }
    }

    const openMiniProgramDialog = () => {
      Object.assign(miniProgramForm, {
        appid: miniProgramConfig.value.appid || '',
        appsecret: miniProgramConfig.value.appsecret || '',
        upload_key: miniProgramConfig.value.upload_key || ''
      })
      miniProgramDialogVisible.value = true
    }

    const saveMiniProgramConfig = async () => {
      if (!miniProgramForm.appid) { ElMessage.warning('请填写AppID'); return }
      saving.value = true
      const loading = ElLoading.service({ text: '保存中...' })
      try {
        const saveData = { appid: miniProgramForm.appid, upload_key: miniProgramForm.upload_key }
        if (miniProgramForm.appsecret) saveData.appsecret = miniProgramForm.appsecret
        else if (miniProgramConfig.value.appsecret) saveData.appsecret = miniProgramConfig.value.appsecret
        else saveData.appsecret = ''

        const response = await axios.post(`${config.BASE_URL}/shop/api/config_api.php`, {
          action: 'save', agent_id: agentId.value, store_id: storeId.value,
          config_key: 'miniprogram', config_value: JSON.stringify(saveData),
          config_group: 'miniprogram', description: '小程序配置'
        })
        if (response.data.code === 200) {
          ElMessage.success('保存成功')
          miniProgramConfig.value = saveData
          miniProgramDialogVisible.value = false
          fetchConfig()
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error('保存失败')
      } finally {
        saving.value = false
        loading.close()
      }
    }

    const openPayDialog = () => {
      Object.assign(payForm, {
        mchid: payConfig.value.mchid || '',
        api_key: payConfig.value.api_key || '',
        api_v3_key: payConfig.value.api_v3_key || '',
        pay_mode: payConfig.value.pay_mode || 'sandbox'
      })
      if (payConfig.value.cert_files) {
        certData.value = { ...payConfig.value.cert_files }
        certFiles.value = Object.keys(certData.value).map((name, index) => ({
          uid: Date.now() + index, name: name, url: certData.value[name]
        }))
      } else {
        certFiles.value = []
        certData.value = {}
      }
      payDialogVisible.value = true
    }

    const handleCertChange = async (file, fileList) => {
      if (!file.raw) return
      if (!file.raw.name.endsWith('.pem')) { ElMessage.error('请上传 .pem 格式的证书文件'); return false }
      if (file.raw.size > 2 * 1024 * 1024) { ElMessage.error('证书文件大小不能超过2MB'); return false }
      try {
        const signature = await getUploadSignature('cert')
        const result = await uploadCertToLocal(file.raw, signature)
        certData.value[file.raw.name] = result.url
        certFiles.value = fileList
        ElMessage.success(`${file.raw.name} 上传成功`)
      } catch (error) {
        console.error('上传失败:', error)
        ElMessage.error(error.message || '上传失败')
      }
    }

    const handleCertRemove = (file) => { delete certData.value[file.name] }

    const savePayConfig = async () => {
      if (!payForm.mchid) { ElMessage.warning('请填写商户号'); return }
      saving.value = true
      const loading = ElLoading.service({ text: '保存中...' })
      try {
        const configValue = { mchid: payForm.mchid, pay_mode: payForm.pay_mode, cert_files: certData.value }
        if (payForm.api_key) configValue.api_key = payForm.api_key
        else if (payConfig.value.api_key) configValue.api_key = payConfig.value.api_key
        else configValue.api_key = ''
        if (payForm.api_v3_key) configValue.api_v3_key = payForm.api_v3_key
        else if (payConfig.value.api_v3_key) configValue.api_v3_key = payConfig.value.api_v3_key
        else configValue.api_v3_key = ''

        const response = await axios.post(`${config.BASE_URL}/shop/api/config_api.php`, {
          action: 'save', agent_id: agentId.value, store_id: storeId.value,
          config_key: 'wechat_pay', config_value: JSON.stringify(configValue),
          config_group: 'payment', description: '微信支付配置'
        })
        if (response.data.code === 200) {
          ElMessage.success('保存成功')
          payConfig.value = configValue
          payDialogVisible.value = false
          fetchConfig()
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error('保存失败')
      } finally {
        saving.value = false
        loading.close()
      }
    }

    const goToPayTest = () => { router.push('/pay-test') }

    onMounted(() => {
      fetchConfig()
      searchBanks('')  // 添加这行，预加载银行列表
    })

    return {
      saving, uploading, uploadProgress, uploadProgressStatus, uploadLogs,
      miniProgramConfig, payConfig,
      miniProgramDialogVisible, payDialogVisible, uploadCodeDialogVisible,
      miniProgramForm, payForm, uploadForm,
      certFiles, certData,
      openMiniProgramDialog, saveMiniProgramConfig,
      openPayDialog, savePayConfig,
      openUploadCodeDialog, submitUploadCode, resetUploadForm,
      handleCertChange, goToPayTest, handleCertRemove,
      latestApply, querying, submittingApply, applyDialogVisible,
      applyForm, applyRules, applyFormRef,
      licenseFileList, idCardFrontFileList, idCardBackFileList,
      hasProcessingApply, statusTitle, statusType,
      openApplyDialog, resetApplyForm, handleImageChange, handleImageRemove,
      submitApply, queryApplyStatus, cancelApply,
      storeEntranceFileList, indoorFileList,
      miniProgramPicsFileList, mpPicsFileList,
      contactIdDocCopyFileList, contactIdDocCopyBackFileList,
      // ✅ 新增
      industryOptions,
      selectedIndustry,
      selectedIndustryInfo,
      needQualifications,
      qualificationsGuide,
      qualificationFileList,
      handleIndustryChange,
      // 银行相关
      bankList,
      bankLoading,
      needBankBranch,
      branchList,
      branchLoading,
      provinceList,
      provinceLoading,
      cityList,
      cityLoading,
      searchBanks,
      onBankChange,
      onAccountTypeChange,
      searchBranches,
      onBranchChange,
      fetchProvinces,
      onProvinceChange,
      onCityChange,
      // 添加这一行！
      reUploadImage,

      signQrDialogVisible,
      signQrUrl,
      copySignUrl,

      showSignQrCode,
      shouldShowSignButton,
      onBankVisibleChange,

    }
  }
}
</script>

<style scoped lang="scss">
.system-setting {
  .page-header {
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  .setting-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
    gap: 24px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .setting-card {
    border-radius: 16px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .card-header-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 16px;
        font-weight: 500;

        .el-icon {
          font-size: 18px;
          color: #409eff;
        }
      }

      .card-header-actions {
        display: flex;
        gap: 12px;
      }
    }

    .config-content {
      .config-item {
        display: flex;
        padding: 12px 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        .config-label {
          width: 100px;
          color: #8c8c8c;
          font-size: 14px;
        }

        .config-value {
          flex: 1;
          color: #333;
          font-size: 14px;
          word-break: break-all;
        }
      }
    }
  }

  .upload-tip {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 8px;
  }

  .form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
    line-height: 1.4;
  }

  .cert-list {
    margin-top: 12px;

    .cert-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      background: #f5f7fa;
      border-radius: 6px;
      margin-bottom: 8px;
      font-size: 13px;

      span {
        color: #333;
        font-family: monospace;
      }
    }
  }

  .upload-progress {
    margin-top: 20px;

    .upload-log {
      margin-top: 12px;
      max-height: 300px;
      overflow-y: auto;
      background: #1e1e1e;
      border-radius: 8px;
      padding: 12px;

      .log-line {
        font-family: 'Monaco', 'Menlo', monospace;
        font-size: 12px;
        color: #d4d4d4;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-all;
      }
    }
  }

  .apply-status {
    .status-detail {
      margin-top: 10px;

      p {
        margin: 5px 0;
        font-size: 14px;
        color: #606266;
      }

      .reject-reason {
        color: #f56c6c;
      }
    }

    .apply-actions {
      margin-top: 12px;
      text-align: right;
    }
  }

  .no-apply {
    padding: 20px 0;
  }

  .upload-wrapper {
    display: inline-block;
    vertical-align: top;

    .upload-label {
      display: block;
      text-align: center;
      font-size: 12px;
      color: #999;
      margin-top: 5px;
    }
  }
}

.uploaded-preview,
.uploaded-item {
  .preview-image {
    position: relative;
    width: 148px;

    .preview-img {
      width: 148px;
      height: 148px;
      object-fit: cover;
      border-radius: 6px;
      border: 1px solid #dcdfe6;
    }

    .preview-actions {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      gap: 8px;
      padding: 8px;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 0 0 6px 6px;
      opacity: 0;
      transition: opacity 0.3s;

      .el-button {
        padding: 4px 8px;
        font-size: 12px;
      }
    }

    &:hover .preview-actions {
      opacity: 1;
    }
  }
}

.id-card-uploads {
  display: flex;
  align-items: flex-start;

  .upload-item,
  .uploaded-item {
    display: inline-block;
    vertical-align: top;
  }
}

.upload-label {
  display: block;
  text-align: center;
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}

.qr-container {
  text-align: center;

  .qr-image {
    width: 200px;
    height: 200px;
    margin: 0 auto;
    border: 1px solid #eee;
    border-radius: 8px;
  }

  .qr-tip {
    margin-top: 16px;
    font-size: 14px;
    color: #666;
  }

  .copy-link {
    margin-top: 12px;
  }
}

.qr-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 40px;
  color: #666;
}

.success-tip {
  margin-top: 8px;
}
</style>